import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

interface MonthSliderProps {
  selectedMonth: number;
  selectedYear: number;
  onMonthSelect: (month: number) => void;
  monthlyTotals: Array<{ month: number; completed_count: number }>;
}

type Month = {
  month: number;
  year: number;
};

const MonthSlider = ({ 
  selectedMonth, 
  selectedYear, 
  onMonthSelect, 
  monthlyTotals 
}: MonthSliderProps) => {
  const { t } = useI18n();
  const [visibleMonths, setVisibleMonths] = useState<Month[]>([]);
  
  const monthNames = [
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
  ];

  useEffect(() => {
    // Generate visible months (current year and adjacent years)
    const months = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // Convert to 1-indexed
    
    // Add previous year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear - 1 });
    }
    
    // Add current year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear });
    }
    
    // Add next year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear + 1 });
    }
    
    setVisibleMonths(months);
    
    // Auto-scroll to current month after a short delay to ensure DOM is ready
    setTimeout(() => {
      scrollToSelectedMonth();
    }, 100);
  }, [selectedMonth, selectedYear]);

  const getTrainingCount = (month: number, year: number) => {
    if (year !== selectedYear) return 0;
    
    const monthData = monthlyTotals.find(m => m.month === month);
    return monthData ? monthData.completed_count : 0;
  };

  const scrollToSelectedMonth = () => {
    const selectedIndex = visibleMonths.findIndex(
      m => m.month === selectedMonth && m.year === selectedYear
    );
    
    if (selectedIndex !== -1) {
      const container = document.getElementById('month-slider-container');
      const item = document.getElementById(`month-item-${selectedIndex}`);
      
      if (container && item) {
        const containerWidth = container.offsetWidth;
        const itemWidth = item.offsetWidth;
        const scrollLeft = item.offsetLeft - (containerWidth / 2) + (itemWidth / 2);
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    scrollToSelectedMonth();
  }, [selectedMonth, selectedYear, visibleMonths.length]);

  return (
    <div className="w-full mb-4">
      <div 
        id="month-slider-container"
        className="flex gap-3 overflow-x-auto scrollbar-hide px-2 py-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {visibleMonths.map(({ month, year }, index) => {
          const isSelected = month === selectedMonth && year === selectedYear;
          const trainingCount = getTrainingCount(month, year);
          
          return (
            <div
              key={`${year}-${month}`}
              id={`month-item-${index}`}
              className={`flex flex-col items-center min-w-[50px] cursor-pointer transition-all duration-200 ${
                isSelected ? 'scale-105' : 'hover:scale-102'
              }`}
              onClick={() => onMonthSelect(month)}
            >
              {/* Month initials */}
              <span className={`text-xs font-medium ${
                isSelected ? 'text-white' : 'text-white/60'
              }`}>
                {t(`calendar.months.${monthNames[month - 1]}`)}
              </span>
              
              {/* Training count with icon */}
              <div className={`flex items-center gap-1 ${
                isSelected ? 'text-white' : 'text-white/40'
              }`}>
                <span className="text-[10px] font-medium">
                  {trainingCount.toString().padStart(2, '0')}
                </span>
                <Calendar className="w-2.5 h-2.5" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { MonthSlider };